import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { GiphyFetch } from '@giphy/js-fetch-api';
import EmojiPicker from 'emoji-picker-react';
import { BarChart3 } from 'lucide-react';

import { FormattedMessage } from '../../../../util/reactIntl';
import useActualNetworkStatus from '../../../../hooks/useActualNetworkStatus';

import {
  Button,
  FieldCheckbox,
  FieldTextInput,
  Form,
  H4,
  ImageFromFile,
  IconSpinner,
  OutsideClickHandler,
  ReactHlsPlayer,
  ResponsiveImage,
  ToolTip,
} from '../../../../components';

import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import { isValidDocumentFile } from '../../../../components/DragAndDrop/DragAndDrop';

import {
  abbreviateName,
  excludedKeysOfCreatePost,
  getPostableGroupsForCreatePost,
  handleFileUpload,
  updatePostFormValues,
} from '../../../../util/circleHelper';
import { required } from '../../../../util/validators';

import FileUploadModal from './FileUploadModal';
import GifUploadModal from './GifUploadModal';
import ImageUploadModal from './ImageUploadModal';
import SelectTopicComponent from './SelectTopicComponent';
import VideoUploadModal from './VideoUploadModal';
import { generateVideoThumbnail, updateUploadedVideoPoster } from './VideoUploadModal';
import { getPostMediaCount, getPostMediaKind, validatePostMediaFiles } from './postMediaUpload';

import css from './CreatePostForm.module.css';
import CircleBlockEditor from '../../../../components/CircleBlockEditor/CircleBlockEditor';
import EditorOptionDropdown from './EditorOptionDropdown';
import { convertToRaw, RichUtils } from 'draft-js';
import CoverImageUploadModal from './CoverImageUploadModal';
import { getCommunityUserRole } from '../../../../util/data';
import useStateRef from '../../../../util/useStateRef';
import CreatePollForm from '../Poll/CreatePollForm';
import {
  createInitialPollState,
  hasMeaningfulPollChanges,
  normalisePollPayload,
  validatePollState,
} from '../Poll/pollFormValidation';

const gf = new GiphyFetch(process.env.REACT_APP_GIPHY_API_KEY);

const POOLSVG = <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <g clip-path="url(#clip0_15039_3999)">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M2.7 5.4C2.93869 5.4 3.16761 5.49482 3.3364 5.6636C3.50518 5.83239 3.6 6.06131 3.6 6.3V17.1C3.6 17.3387 3.50518 17.5676 3.3364 17.7364C3.16761 17.9052 2.93869 18 2.7 18H0.9C0.661305 18 0.432387 17.9052 0.263604 17.7364C0.0948212 17.5676 0 17.3387 0 17.1V6.3C0 6.06131 0.0948212 5.83239 0.263604 5.6636C0.432387 5.49482 0.661305 5.4 0.9 5.4H2.7ZM7.5285 10.8C7.76719 10.8 7.99611 10.8948 8.1649 11.0636C8.33368 11.2324 8.4285 11.4613 8.4285 11.7V17.1C8.4285 17.3387 8.33368 17.5676 8.1649 17.7364C7.99611 17.9052 7.76719 18 7.5285 18H5.7285C5.4898 18 5.26089 17.9052 5.0921 17.7364C4.92332 17.5676 4.8285 17.3387 4.8285 17.1V11.7C4.8285 11.4613 4.92332 11.2324 5.0921 11.0636C5.26089 10.8948 5.4898 10.8 5.7285 10.8H7.5285ZM12.3273 0C12.566 0 12.7949 0.0948212 12.9637 0.263604C13.1325 0.432387 13.2273 0.661305 13.2273 0.9V17.1C13.2273 17.3387 13.1325 17.5676 12.9637 17.7364C12.7949 17.9052 12.566 18 12.3273 18H10.5273C10.2886 18 10.0597 17.9052 9.8909 17.7364C9.72212 17.5676 9.6273 17.3387 9.6273 17.1V0.9C9.6273 0.661305 9.72212 0.432387 9.8909 0.263604C10.0597 0.0948212 10.2886 0 10.5273 0L12.3273 0ZM17.1 0C17.3387 0 17.5676 0.0948212 17.7364 0.263604C17.9052 0.432387 18 0.661305 18 0.9V17.1C18 17.3387 17.9052 17.5676 17.7364 17.7364C17.5676 17.9052 17.3387 18 17.1 18H15.3C15.0613 18 14.8324 17.9052 14.6636 17.7364C14.4948 17.5676 14.4 17.3387 14.4 17.1V0.9C14.4 0.661305 14.4948 0.432387 14.6636 0.263604C14.8324 0.0948212 15.0613 0 15.3 0L17.1 0Z" fill="#403D45"/>
  </g>
  <defs>
    <clipPath id="clip0_15039_3999">
      <rect width="18" height="18" fill="white"/>
    </clipPath>
  </defs>
</svg>;

const getMediaUrl = media =>
  media?.thq_media_url ||
  media?.url ||
  media?.image_variants?.large ||
  media?.image_variants?.small ||
  media?.image_variants?.thumbnail ||
  media?.file ||
  '';

const getVideoUrl = video =>
  video?.video_variants?.hls || video?.video_variants?.mp4 || getMediaUrl(video);

const getMediaIdentity = media =>
  media?.signed_id || media?.id || getVideoUrl(media) || media?.filename || media?.name || '';

const CreatePostFormFields = fieldRenderProps => {
  const {
    form,
    handleSubmit,
    invalid,
    spaceListData,
    isEmoji,
    setIsEmoji,
    values,
    requestDirectUpload,
    onGiphyChange,
    onDocumentClick,
    onImageClick,
    onVideoClick,
    isGiphyOpen,
    isDocumentModal,
    isImageModal,
    isVideoUpload,
    formOldValues,
    setFormOldValues,
    onFetchTopics,
    spaceTopicsData,
    advancedSearchData,
    onAdvancedSearch,
    params,
    editorState,
    setEditorState,
    insertEmoji,
    isCoverImageModal,
    onCoverImageClick,
    userModeratorsGroups,
    requestCreatePost,
    currentUser,
    isEditMode = false,
  } = fieldRenderProps;
  const { id } = params || {};
  const [isSpaceDropdownVisible, setIsSpaceDropdownVisible] = useStateRef(false);
  const [isEdited, setIsEdited, isEditedRef] = useStateRef(false);
  const [isEditorOptionOpen, setIsEditorOptionOpen] = useStateRef(false);
  const [limitExceed, setLimitExceed, limitExceedRef] = useStateRef(false);
  const { isOnline } = useActualNetworkStatus();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [isNotificationDropdownVisible, setIsNotificationDropdownVisible] = useStateRef(false);
  const [dropUploadError, setDropUploadError] = useState(null);
  const [isDropUploading, setIsDropUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [composerMode, setComposerMode] = useState('post');
  const [pollState, setPollState] = useState(() => createInitialPollState());
  const [latestPollOptionId, setLatestPollOptionId] = useState(null);
  const dragCounterRef = useRef(0);
  const dispatch = useDispatch();
  const isPollMode = composerMode === 'poll' && !isEditMode;
  const pollValidation = validatePollState(pollState);
  const hasPollChanges = hasMeaningfulPollChanges(pollState);

  const hasAttachment =
    Boolean(formOldValues.image.length) ||
    Boolean(formOldValues.gifs.length) ||
    Boolean(formOldValues.video.length) ||
    Boolean(formOldValues.document.length);

  const validationMessage = <FormattedMessage id="CreatePost.validationMessage" />;

  const handleEmoji = emoji => {
    insertEmoji(emoji.emoji);
    setIsEmoji(false);
    return;
  };

  // Function to toggle the visibility of the box
  const toggleBox = () => {
    if (isEditMode) {
      return;
    }
    setIsSpaceDropdownVisible(!isSpaceDropdownVisible);
  };

  // Function to toggle notification dropdown
  const toggleNotificationDropdown = () => {
    setIsNotificationDropdownVisible(!isNotificationDropdownVisible);
  };

  const handleSpaceSelection = spaceId => {
    updatePostFormValues(spaceId, setFormOldValues, 'spaceId');
    updatePostFormValues([], setFormOldValues, 'topics');

    // Also update FinalForm state to keep them in sync
    form.change('spaceId', spaceId);
    form.change('topics', []);

    onFetchTopics(spaceId);
    setIsSpaceDropdownVisible(false);
  };

  useEffect(() => {
    if (id && values.spaceId !== id) {
      handleSpaceSelection(id);
    }
  }, [id]);

  const selectedSpaceName = spaceListData?.find(item => item.id == values.spaceId)?.name;
  const handleRemoveItem = (item, type, fileType) => {
    const pairedFileType = {
      image: 'imagefiles',
      gifs: 'giffiles',
      video: 'videofiles',
      document: 'documentsfiles',
    }[type];
    const fieldsToUpdate = [type, fileType, pairedFileType].filter(Boolean);
    const itemIdentity = getMediaIdentity(item);
    const removeItem = value => {
      const valueIdentity = getMediaIdentity(value);
      return itemIdentity ? valueIdentity !== itemIdentity : value !== item;
    };

    // Update formOldValues state
    setFormOldValues(prevState => {
      return fieldsToUpdate.reduce(
        (updatedValues, fieldName) => ({
          ...updatedValues,
          [fieldName]: (updatedValues[fieldName] || []).filter(removeItem),
        }),
        prevState
      );
    });

    // Also update FinalForm state to keep them in sync
    fieldsToUpdate.forEach(fieldName => {
      form.change(fieldName, (values[fieldName] || []).filter(removeItem));
    });
  };

  const playerRef = useRef(null);

  useEffect(() => {
    if (playerRef.current) {
      const video = playerRef.current.querySelector('video');
      if (video) {
        video.setAttribute('playsinline', 'true');
        video.setAttribute('muted', 'true'); // Mute for Safari autoplay policies
      }
    }
  }, [playerRef]);

  const toggleDropdown = () => {
    setIsEditorOptionOpen(prev => !prev);
  };

  const toggleBlockType = blockType => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  const toggleInlineStyle = inlineStyle => {
    // Simply toggle the inline style at the current cursor position
    // This will apply to future typed text without selecting existing text
    setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const getCurrentBlockType = () => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());
    return block.getType();
  };

  const currentBlockType = getCurrentBlockType();

  const getCurrentInlineStyle = () => {
    return editorState.getCurrentInlineStyle();
  };

  const currentInlineStyle = getCurrentInlineStyle();

  const handleChange = event => {
    const newValue = event?.target?.value;

    // Update formOldValues state
    setFormOldValues(prevState => {
      const updatedValues = {
        ...prevState,
        postTitle: newValue,
      };
      return updatedValues;
    });

    // Also update FinalForm state to keep them in sync
    form.change('postTitle', newValue);
  };

  const handleRemoveCoverImage = () => {
    // Update formOldValues state
    setFormOldValues(prevState => {
      const updatedValues = {
        ...prevState,
        coverImage: null,
        coverImagefiles: null,
      };
      return updatedValues;
    });

    // Also update FinalForm state to keep them in sync
    form.change('coverImage', null);
    form.change('coverImagefiles', null);
  };

  const handleEditorChange = useCallback(
    newEditorState => {
      setEditorState(newEditorState);
      // Convert editor content to raw format and check if there's meaningful text
      const content = convertToRaw(newEditorState.getCurrentContent());
      const hasText = content.blocks.some(block => block.text.trim().length > 0);

      if (hasText !== isEditedRef.current) {
        setIsEdited(hasText);
      }
    },
    [isEditedRef, setEditorState, setIsEdited]
  );

  const handleLimitExceedChange = useCallback(
    nextLimitExceed => {
      if (nextLimitExceed !== limitExceedRef.current) {
        setLimitExceed(nextLimitExceed);
      }
    },
    [limitExceedRef, setLimitExceed]
  );

  const { spaceId = '', topics = [] } = values ?? {};
  const isFilled = Object.entries(values).some(([key, value]) => {
    if (excludedKeysOfCreatePost.includes(key)) return false;

    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;

    return value !== undefined && value !== '' && value !== null;
  });

  const currentContent = editorState ? editorState.getCurrentContent() : null;
  const description =
    currentContent && currentContent.getPlainText('') && currentContent.getPlainText('').trim()
      ? true
      : false;

  const postTitle = formOldValues?.postTitle && formOldValues?.postTitle.trim();
  const isTopicSelectionRequired =
    Object.values(spaceListData).find(item => item.id === Number(spaceId))
      ?.require_topic_selection ?? false;
  const isPostValid =
    (postTitle || description) &&
    (isFilled || isEdited) &&
    (!isTopicSelectionRequired || (isTopicSelectionRequired && topics.length > 0)) &&
    spaceId !== '';
  const isPollValid =
    pollValidation.isValid &&
    (!isTopicSelectionRequired || (isTopicSelectionRequired && topics.length > 0)) &&
    spaceId !== '';
  const isValid = isPollMode ? isPollValid : isPostValid;

  const userRoleInGroup = spaceId ? getCommunityUserRole(userModeratorsGroups, spaceId) : null;
  const isUploadDisabled =
    requestDirectUpload || requestCreatePost || isDropUploading || !isOnline || isPollMode;

  const resetPollComposer = () => {
    const nextState = createInitialPollState();
    setPollState(nextState);
    setLatestPollOptionId(nextState.options[0]?.localId || null);
  };

  useEffect(() => {
    if (requestCreatePost) {
      return;
    }

    if (!hasPollChanges && !isPollMode) {
      resetPollComposer();
    }
  }, [hasPollChanges, isPollMode, requestCreatePost]);

  const togglePollMode = () => {
    if (requestCreatePost || isEditMode) {
      return;
    }

    setComposerMode(prevMode => (prevMode === 'poll' ? 'post' : 'poll'));
  };

  const commitDroppedMediaUploads = successfulUploads => {
    const imageUploads = successfulUploads.filter(upload => upload.kind === 'image');
    const videoUploads = successfulUploads
      .map(upload => {
        if (upload.kind !== 'video') return upload;
        return {
          ...upload,
          previewUrl: URL.createObjectURL(upload.file),
        };
      })
      .filter(upload => upload.kind === 'video');

    const currentValues = form.getState?.().values || values || {};
    const newImages = imageUploads.map(upload => upload.response);
    const newImageFiles = imageUploads.map(upload => ({
      file: upload.file,
      signed_id: upload.response.signed_id,
    }));
    const newVideos = videoUploads.map(upload => upload.response);
    const newVideoFiles = videoUploads.map(upload => ({
      file: upload.previewUrl,
      poster: null,
      signed_id: upload.response.signed_id,
    }));

    setFormOldValues(previousValues => ({
      ...previousValues,
      image: [...(previousValues?.image || []), ...newImages],
      imagefiles: [...(previousValues?.imagefiles || []), ...newImageFiles],
      video: [...(previousValues?.video || []), ...newVideos],
      videofiles: [...(previousValues?.videofiles || []), ...newVideoFiles],
    }));

    form.change('image', [...(currentValues.image || []), ...newImages]);
    form.change('imagefiles', [...(currentValues.imagefiles || []), ...newImageFiles]);
    form.change('video', [...(currentValues.video || []), ...newVideos]);
    form.change('videofiles', [...(currentValues.videofiles || []), ...newVideoFiles]);

    videoUploads.forEach(upload => {
      generateVideoThumbnail(upload.previewUrl)
        .then(poster =>
          updateUploadedVideoPoster({
            form,
            setFormOldValues,
            signedId: upload.response.signed_id,
            poster,
          })
        )
        .catch(error => console.error('Video thumbnail generation failed:', error));
    });
  };

  const uploadDroppedMedia = async mediaFiles => {
    if (!mediaFiles.length) {
      return [];
    }

    const currentValues = form.getState?.().values || values || {};
    const existingCount = Math.max(
      getPostMediaCount(currentValues),
      getPostMediaCount(formOldValues)
    );
    const { acceptedFiles, errors: validationErrors } = validatePostMediaFiles({
      files: mediaFiles,
      existingCount,
    });

    const errors = [...validationErrors];
    if (!acceptedFiles.length) {
      return errors;
    }

    const results = await Promise.allSettled(
      acceptedFiles.map(file => handleFileUpload(file, dispatch))
    );
    const successfulUploads = [];
    const failedFiles = [];

    results.forEach((result, index) => {
      const file = acceptedFiles[index];
      if (result.status === 'fulfilled' && result.value?.signed_id) {
        successfulUploads.push({
          file,
          kind: getPostMediaKind(file),
          response: result.value,
        });
      } else {
        failedFiles.push(file.name);
      }
    });

    if (successfulUploads.length) {
      commitDroppedMediaUploads(successfulUploads);
    }

    if (failedFiles.length) {
      errors.push(`Failed to upload ${failedFiles.length} file(s): ${failedFiles.join(', ')}.`);
    }

    return errors;
  };

  const uploadDroppedDocuments = async documentFiles => {
    if (!documentFiles.length) {
      return [];
    }

    const results = await Promise.allSettled(
      documentFiles.map(file => handleFileUpload(file, dispatch))
    );
    const successfulDocuments = [];
    const failedFiles = [];

    results.forEach((result, index) => {
      const file = documentFiles[index];
      if (result.status === 'fulfilled' && result.value?.signed_id) {
        successfulDocuments.push({
          file,
          response: result.value,
        });
      } else {
        failedFiles.push(file.name);
      }
    });

    if (successfulDocuments.length) {
      const currentValues = form.getState?.().values || values || {};
      const newDocuments = successfulDocuments.map(upload => upload.response);
      const newDocumentFiles = successfulDocuments.map(upload => ({
        file: upload.file,
        signed_id: upload.response.signed_id,
      }));

      setFormOldValues(previousValues => ({
        ...previousValues,
        document: [...(previousValues?.document || []), ...newDocuments],
        documentsfiles: [...(previousValues?.documentsfiles || []), ...newDocumentFiles],
      }));

      form.change('document', [...(currentValues.document || []), ...newDocuments]);
      form.change('documentsfiles', [...(currentValues.documentsfiles || []), ...newDocumentFiles]);
    }

    return failedFiles.length
      ? [`Failed to upload ${failedFiles.length} file(s): ${failedFiles.join(', ')}.`]
      : [];
  };

  const handleDroppedFiles = async selectedFiles => {
    const files = Array.from(selectedFiles || []);
    if (!files.length || isUploadDisabled) {
      return;
    }

    const mediaFiles = files.filter(file => getPostMediaKind(file));
    const documentFiles = files.filter(isValidDocumentFile);
    const unsupportedFiles = files.filter(
      file => !getPostMediaKind(file) && !isValidDocumentFile(file)
    );

    setDropUploadError(null);
    setIsDropUploading(true);

    try {
      const uploadErrors = [
        ...(unsupportedFiles.length
          ? [
            `${unsupportedFiles
              .map(file => file.name)
              .join(', ')}: only images, videos, PDF, DOC, and DOCX files are supported.`,
          ]
          : []),
        ...(await uploadDroppedMedia(mediaFiles)),
        ...(await uploadDroppedDocuments(documentFiles)),
      ];

      setDropUploadError(uploadErrors.length ? uploadErrors.join(' ') : null);
    } catch (error) {
      console.error('Create post drop upload failed:', error);
      setDropUploadError('An unexpected error occurred during upload. Please try again.');
    } finally {
      setIsDropUploading(false);
    }
  };

  const preventDragDefaults = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = event => {
    preventDragDefaults(event);
    if (isUploadDisabled) {
      return;
    }
    dragCounterRef.current += 1;
    if (event.dataTransfer?.items?.length) {
      setIsDragActive(true);
    }
  };

  const handleDragOver = event => {
    preventDragDefaults(event);
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = isUploadDisabled ? 'none' : 'copy';
    }
  };

  const handleDragLeave = event => {
    preventDragDefaults(event);
    if (isUploadDisabled) {
      return;
    }
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragActive(false);
    }
  };

  const handleDrop = event => {
    preventDragDefaults(event);
    const { dataTransfer } = event;
    dragCounterRef.current = 0;
    setIsDragActive(false);
    handleDroppedFiles(dataTransfer?.files);
    dataTransfer?.clearData?.();
  };

  return (
    <Form
      onSubmit={e => {
        e.preventDefault();
        if (isPollMode) {
          const { validation, payload } = normalisePollPayload(pollState);

          if (!validation.isValid) {
            setPollState(prevState => ({
              ...prevState,
              titleError: validation.titleError,
              formError: validation.formError,
              formNotice: null,
              options: validation.options.map(option => ({
                ...option,
                isEditing: Boolean(option.error) || option.isEditing,
              })),
            }));
            return;
          }

          form.change('__pollPayload', payload);
          form.change('__pollClientRequestId', pollState.clientRequestId);
        } else {
          form.change('__pollPayload', undefined);
          form.change('__pollClientRequestId', undefined);
        }
        handleSubmit(e);
      }}
    >
      <div
        className={classNames(css.createPostTop, { [css.createPostDragActive]: isDragActive })}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={css.topHeadingWrapper}>
          <H4>
            {isEditMode ? 'Edit post' : isPollMode ? 'Create A Post' : 'Create post'}
            <span className={css.tooltipWrapper}>
              {/* <ToolTip
                    headingText={
                      'You need to enter at least a title or a description to create your post.'
                    }
                  /> */}
            </span>
          </H4>

          {/* <div onClick={onCoverImageClick}><ProfileIconCard type="cover"/></div> */}
        </div>

        <div className={isMobile ? css.mobileEditorSection : ''}>
          <div className={css.postTitleWrapper}>
            {isPollMode ? (
              <CreatePollForm
                pollState={pollState}
                setPollState={setPollState}
                disabled={requestCreatePost}
                latestOptionId={latestPollOptionId}
                setLatestOptionId={setLatestPollOptionId}
              />
            ) : values?.coverImagefiles?.file ? (
              <div className={css.imageCoverWrapper}>
                <div className={css.removeIcon} onClick={handleRemoveCoverImage}>
                  <ProfileIconCard type="cross" />
                </div>
                <ImageFromFile
                  id={values?.coverImagefiles?.file}
                  className={''}
                  file={values?.coverImagefiles?.file}
                  aspectWidth={1}
                  aspectHeight={1}
                />
              </div>
            ) : null}
            {!isPollMode ? (
              <div className={css.inputPostWrapper}>
                <input
                  className={css.postTitleField}
                  id="postTitle"
                  type="text"
                  value={formOldValues?.postTitle}
                  onChange={handleChange}
                  placeholder="Title (optional)"
                  maxLength={250}
                />
                <div className={css.suggestField}>
                  <CircleBlockEditor
                    hasAttachment={hasAttachment}
                    editorState={editorState}
                    setEditorState={handleEditorChange}
                    advancedSearchData={advancedSearchData}
                    onAdvancedSearch={onAdvancedSearch}
                    currentBlockType={currentBlockType}
                    setLimitExceed={handleLimitExceedChange}
                  />
                </div>
              </div>
            ) : null}{' '}
            <br />
            {!isPollMode && limitExceed ? (
              <span className={css.errorText}>
                <FormattedMessage id="CreatePost.errorText" />
              </span>
            ) : null}
            {!isPollMode && isDropUploading ? (
              <div className={css.dropUploadStatus}>
                <IconSpinner type={true} />
                <span>Uploading dropped files...</span>
              </div>
            ) : null}
            {!isPollMode && dropUploadError ? (
              <span className={css.errorText}>{dropUploadError}</span>
            ) : null}
            {!isPollMode ? (
              <>
                {/* render items from edit comment section */}
                <div className={css.imageuploadedWrapper}>
                  {Array.isArray(values?.image)
                    ? values?.image.map(file => {
                      const imageUrl = getMediaUrl(file);
                      if (imageUrl) {
                        return (
                          <div key={getMediaIdentity(file)}>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'image', '')}
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                            <ResponsiveImage imagewithURL={imageUrl} />
                          </div>
                        );
                      }
                      return null;
                    })
                    : null}
                </div>
                <div className={css.gifPreviewWrapper}>
                  {Array.isArray(values?.gifs)
                    ? values?.gifs.map(file => {
                      const gifUrl = getMediaUrl(file);
                      if (gifUrl)
                        return (
                          <div className={css.gifPreviewItem} key={getMediaIdentity(file)}>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'gifs', '')}
                              onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  handleRemoveItem(file, 'gifs', '');
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              aria-label="Remove GIF"
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                            <img src={gifUrl} alt={file?.filename || 'GIF preview'} />
                          </div>
                        );
                      return null;
                    })
                    : null}
                </div>
                <div className={css.videoWrapper}>
                  {Array.isArray(values?.video)
                    ? values?.video.map(file => {
                      const videoUrl = getVideoUrl(file);
                      if (videoUrl)
                        return (
                          <div key={getMediaIdentity(file)}>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'video', '')}
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                            {file?.video_variants?.hls || /\.m3u8(?:$|\?)/i.test(videoUrl) ? (
                              <ReactHlsPlayer
                                src={videoUrl}
                                autoPlay={true}
                                controls={true}
                                width="100%"
                                height="auto"
                                poster={file?.image_variants?.thumbnail}
                              />
                            ) : (
                              <video
                                src={videoUrl}
                                poster={file?.image_variants?.thumbnail}
                                controls
                                muted
                                playsInline
                                preload="metadata"
                              />
                            )}
                          </div>
                        );
                      return null;
                    })
                    : null}
                </div>
                <div className={css.fileUplaodedWrapper}>
                  {Array.isArray(values?.document)
                    ? values?.document.map(file => {
                      return (
                        <div>
                          <div className={css.fileUploaded} key={file}>
                            <span>
                              <ProfileIconCard type="document" />
                              {file?.filename}
                            </span>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'document', '')}
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                          </div>
                        </div>
                      );
                    })
                    : null}
                </div>
                {/* render images */}
                <div className={css.imageuploadedWrapper}>
                  {Array.isArray(values?.imagefiles)
                    ? values?.imagefiles.map((file, index) => {
                      const renderedImageIds = new Set(
                        (Array.isArray(values?.image) ? values.image : [])
                          .filter(getMediaUrl)
                          .map(getMediaIdentity)
                          .filter(Boolean)
                      );
                      const fileIdentity = getMediaIdentity(file);
                      if (fileIdentity && renderedImageIds.has(fileIdentity)) return null;

                      return (
                        <div key={fileIdentity || index}>
                          <div
                            className={css.removeIcon}
                            onClick={() => handleRemoveItem(file, 'image', 'imagefiles')}
                          >
                            <ProfileIconCard type="cross" />
                          </div>
                          {typeof file.file === 'string' ? (
                            <img src={file.file} alt="" />
                          ) : (
                            <ImageFromFile
                              id={file.file.name}
                              className={''}
                              file={file.file}
                              aspectWidth={1}
                              aspectHeight={1}
                            />
                          )}
                        </div>
                      );
                    })
                    : null}
                </div>
                {/* render gifs */}
                <div className={css.gifPreviewWrapper}>
                  {Array.isArray(values?.giffiles)
                    ? values?.giffiles.map((file, index) => {
                      const renderedGifIds = new Set(
                        (Array.isArray(values?.gifs) ? values.gifs : [])
                          .filter(getMediaUrl)
                          .map(getMediaIdentity)
                          .filter(Boolean)
                      );
                      const fileIdentity = getMediaIdentity(file);
                      if (fileIdentity && renderedGifIds.has(fileIdentity)) return null;

                      return (
                        <div className={css.gifPreviewItem} key={fileIdentity || index}>
                          <div
                            className={css.removeIcon}
                            onClick={() => handleRemoveItem(file, 'gifs', 'giffiles')}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleRemoveItem(file, 'gifs', 'giffiles');
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="Remove GIF"
                          >
                            <ProfileIconCard type="cross" />
                          </div>
                          <img src={file.file} alt="GIF preview" />
                        </div>
                      );
                    })
                    : null}
                </div>
                {/* render videos */}
                <div className={css.videoWrapper}>
                  {Array.isArray(values?.videofiles)
                    ? values?.videofiles.map((file, index) => {
                      const existingVideoIds = new Set(
                        (Array.isArray(values?.video) ? values.video : [])
                          .map(getMediaIdentity)
                          .filter(Boolean)
                      );
                      const fileIdentity = getMediaIdentity(file);
                      if (fileIdentity && existingVideoIds.has(fileIdentity)) {
                        return null;
                      }

                      return (
                        <div key={fileIdentity || index}>
                          <div
                            className={css.removeIcon}
                            onClick={() => handleRemoveItem(file, 'video', 'videofiles')}
                          >
                            <ProfileIconCard type="cross" />
                          </div>
                          <video
                            src={file.file}
                            poster={file.poster}
                            loop
                            autoPlay
                            muted
                            controls
                            playsInline
                            webkit-playsinline="true"
                            preload="metadata"
                          />
                        </div>
                      );
                    })
                    : null}
                </div>
              </>
            ) : null}
            {/* render documents */}
            {/* <div className={css.fileUplaodedWrapper}>
              {Array.isArray(values?.documentsfiles)
                ? values?.documentsfiles.map(file => {
                    return (
                      <div className={css.fileUploaded} key={file}>
                        {file.name}
                      </div>
                    );
                  })
                : null}
            </div> */}
            {/* {requestDirectUpload ? (
              <div className={css.loader}>
                <IconSpinner type={true} />
              </div>
            ) : null} */}
          </div>

          {isMobile ? (
            <div className={css.leftIconMobileSection}>
              {!isPollMode ? (
                <>
                  <div className={css.documentUpload}>
                    {/* //isEditorOptionOpen, setIsEditorOptionOpen */}
                    <OutsideClickHandler
                      onOutsideClick={() => isEditorOptionOpen && setIsEditorOptionOpen(false)}
                    >
                      <label
                        data-testid="add-file-button-mobile"
                        className={css.uplaodIcon}
                        onClick={toggleDropdown}
                      >
                        <ProfileIconCard type="add_file" />
                      </label>
                      <div className={css.editDropdown}>
                        {isEditorOptionOpen ? (
                          <EditorOptionDropdown
                            toggleDropdown={toggleDropdown}
                            toggleBlockType={toggleBlockType}
                            toggleInlineStyle={toggleInlineStyle}
                            currentBlockType={currentBlockType}
                            currentInlineStyle={currentInlineStyle}
                          />
                        ) : null}
                      </div>
                    </OutsideClickHandler>
                  </div>
                  <div className={css.inputField}>
                    <label
                      data-testid="document-upload-mobile"
                      className={css.uplaodIcon}
                      onClick={onDocumentClick}
                    >
                      <ProfileIconCard type="document_uplaod" />
                    </label>
                    {isDocumentModal ? (
                      <FileUploadModal
                        isOnline={isOnline}
                        isDocumentModal={isDocumentModal}
                        onDocumentClick={onDocumentClick}
                        form={form}
                        values={values}
                        requestDirectUpload={requestDirectUpload}
                        formOldValues={formOldValues}
                        setFormOldValues={setFormOldValues}
                      />
                    ) : null}
                  </div>

                  <div className={css.gifSection}>
                    <div
                      data-testid="gif-button-mobile"
                      className={css.uplaodIcon}
                      onClick={onGiphyChange}
                    >
                      <ProfileIconCard type="gif" />
                    </div>

                    {isGiphyOpen ? (
                      <GifUploadModal
                        isGiphyOpen={isGiphyOpen}
                        onGiphyChange={onGiphyChange}
                        form={form}
                        values={values}
                        requestDirectUpload={requestDirectUpload}
                        formOldValues={formOldValues}
                        setFormOldValues={setFormOldValues}
                      />
                    ) : null}
                  </div>
                  <div className={css.inputField}>
                    <label
                      data-testid="image-upload-button-mobile"
                      htmlFor="fileInput"
                      className={css.uplaodIcon}
                      onClick={onImageClick}
                    >
                      <ProfileIconCard type="image" />
                    </label>
                    {isImageModal && isOnline ? (
                      <ImageUploadModal
                        isImageModal={isImageModal}
                        onImageClick={onImageClick}
                        form={form}
                        values={values}
                        requestDirectUpload={requestDirectUpload}
                        formOldValues={formOldValues}
                        setFormOldValues={setFormOldValues}
                      />
                    ) : null}
                  </div>
                  <div className={css.inputField}>
                    <label
                      data-testid="video-upload-mobile"
                      className={css.uplaodIcon}
                      onClick={onVideoClick}
                    >
                      <ProfileIconCard type="video" />
                    </label>
                    <VideoUploadModal
                      isOnline={isOnline}
                      isVideoUpload={isVideoUpload}
                      onVideoClick={onVideoClick}
                      form={form}
                      values={values}
                      requestDirectUpload={requestDirectUpload}
                      formOldValues={formOldValues}
                      setFormOldValues={setFormOldValues}
                    />
                  </div>
                  <OutsideClickHandler onOutsideClick={() => isEmoji && setIsEmoji(false)}>
                    <div className={css.emojiSection}>
                      <div
                        data-testid="emoji-upload-mobile"
                        className={css.uplaodIcon}
                        onClick={() => setIsEmoji(!isEmoji)}
                      >
                        <ProfileIconCard type="emoji" />
                      </div>

                      <div className={css.emojiPick}>
                        {isEmoji ? <EmojiPicker onEmojiClick={handleEmoji} /> : null}
                      </div>
                    </div>
                  </OutsideClickHandler>
                </>
              ) : null}
              {!isEditMode ? (
                <button
                  type="button"
                  className={classNames(css.pollModeButton, {
                    [css.pollModeButtonActive]: isPollMode,
                  })}
                  disabled={requestCreatePost}
                  aria-pressed={isPollMode}
                  aria-label={isPollMode ? 'Return to post mode' : 'Create a poll'}
                  onClick={togglePollMode}
                >
                  {POOLSVG}
                  {/* <BarChart3 aria-hidden="true" /> */}
                </button>
              ) : null}
              <SelectTopicComponent
                spaceTopicsData={spaceTopicsData}
                setFormOldValues={setFormOldValues}
                formOldValues={formOldValues}
                form={form}
              />
              {/* <div className={css.uplaodIcon} >
                  <ProfileIconCard type="marketplace" />
                </div> */}
            </div>
          ) : null}
        </div>

        {/* select topic component */}
        {isMobile ? null : (
          <SelectTopicComponent
            spaceTopicsData={spaceTopicsData}
            setFormOldValues={setFormOldValues}
            formOldValues={formOldValues}
            form={form}
          />
        )}
        <div className={css.bottomFunctionMenu}>
          {isMobile ? null : (
            <div className={css.leftIconSection}>
              {!isPollMode ? (
                <>
                  <div className={css.documentUpload}>
                    {/* //isEditorOptionOpen, setIsEditorOptionOpen */}
                    <OutsideClickHandler
                      onOutsideClick={() => isEditorOptionOpen && setIsEditorOptionOpen(false)}
                    >
                      <label
                        data-testid="add-file-button"
                        className={css.uplaodIcon}
                        onClick={toggleDropdown}
                      >
                        <ProfileIconCard type="add_file" />
                      </label>
                      <div className={css.editDropdown}>
                        {isEditorOptionOpen ? (
                          <EditorOptionDropdown
                            toggleDropdown={toggleDropdown}
                            toggleBlockType={toggleBlockType}
                            toggleInlineStyle={toggleInlineStyle}
                            currentBlockType={currentBlockType}
                            currentInlineStyle={currentInlineStyle}
                          />
                        ) : null}
                      </div>
                    </OutsideClickHandler>
                  </div>
                  <div className={css.inputField}>
                    <label
                      data-testid="document-upload-button"
                      className={css.uplaodIcon}
                      onClick={onDocumentClick}
                    >
                      <ProfileIconCard type="document_uplaod" />
                    </label>
                    {isDocumentModal ? (
                      <FileUploadModal
                        isDocumentModal={isDocumentModal}
                        onDocumentClick={onDocumentClick}
                        form={form}
                        isOnline={isOnline}
                        values={values}
                        requestDirectUpload={requestDirectUpload}
                        formOldValues={formOldValues}
                        setFormOldValues={setFormOldValues}
                      />
                    ) : null}
                  </div>

                  <div className={css.gifSection}>
                    <div
                      data-testid="gif-upload-button"
                      className={css.uplaodIcon}
                      onClick={onGiphyChange}
                    >
                      <ProfileIconCard type="gif" />
                    </div>

                    {isGiphyOpen ? (
                      <GifUploadModal
                        isGiphyOpen={isGiphyOpen}
                        onGiphyChange={onGiphyChange}
                        form={form}
                        values={values}
                        requestDirectUpload={requestDirectUpload}
                        formOldValues={formOldValues}
                        setFormOldValues={setFormOldValues}
                      />
                    ) : null}
                  </div>
                  <div className={css.inputField}>
                    <label
                      data-testid="image-upload-button"
                      htmlFor="fileInput"
                      className={css.uplaodIcon}
                      onClick={onImageClick}
                    >
                      <ProfileIconCard type="image" />
                    </label>
                    {isImageModal && isOnline ? (
                      <ImageUploadModal
                        isImageModal={isImageModal}
                        onImageClick={onImageClick}
                        form={form}
                        values={values}
                        requestDirectUpload={requestDirectUpload}
                        formOldValues={formOldValues}
                        setFormOldValues={setFormOldValues}
                      />
                    ) : null}
                  </div>
                  <div className={css.inputField}>
                    <label
                      data-testid="video-upload-button"
                      className={css.uplaodIcon}
                      onClick={onVideoClick}
                    >
                      <ProfileIconCard type="video" />
                    </label>
                    <VideoUploadModal
                      isOnline={isOnline}
                      isVideoUpload={isVideoUpload}
                      onVideoClick={onVideoClick}
                      form={form}
                      values={values}
                      requestDirectUpload={requestDirectUpload}
                      formOldValues={formOldValues}
                      setFormOldValues={setFormOldValues}
                    />
                  </div>
                  <OutsideClickHandler onOutsideClick={() => isEmoji && setIsEmoji(false)}>
                    <div data-testid="emoji-section" className={css.emojiSection}>
                      <div className={css.uplaodIcon} onClick={() => setIsEmoji(!isEmoji)}>
                        <ProfileIconCard type="emoji" />
                      </div>

                      <div className={css.emojiPick}>
                        {isEmoji ? <EmojiPicker onEmojiClick={handleEmoji} /> : null}
                      </div>
                    </div>
                  </OutsideClickHandler>
                </>
              ) : null}
              {!isEditMode ? (
                <button
                  type="button"
                  className={classNames(css.pollModeButton, {
                    [css.pollModeButtonActive]: isPollMode,
                  })}
                  disabled={requestCreatePost}
                  aria-pressed={isPollMode}
                  aria-label={isPollMode ? 'Return to post mode' : 'Create a poll'}
                  onClick={togglePollMode}
                >
                  {POOLSVG}
                  {/* <BarChart3 aria-hidden="true" /> */}
                </button>
              ) : null}
              {/* <div className={css.uplaodIcon} >
                  <ProfileIconCard type="marketplace" />
                </div> */}
            </div>
          )}
          <div className={css.rightFieldSection}>
            <Field
              name="spaceId"
              validate={required(validationMessage)}
              render={({ meta }) => {
                return (
                  <div className={css.spaceIdBox}>
                    <div
                      className={classNames(css.selectInput, {
                        [css.selectInputDisabled]: isEditMode,
                      })}
                      onClick={toggleBox}
                      aria-disabled={isEditMode}
                    >
                      <span>
                        {selectedSpaceName || (
                          <FormattedMessage id="CreatePost.chooseSpacePlaceHolder" />
                        )}
                      </span>
                    </div>

                    {isSpaceDropdownVisible && (
                      <OutsideClickHandler onOutsideClick={() => setIsSpaceDropdownVisible(false)}>
                        <div className={css.searchSpaceBox}>
                          {getPostableGroupsForCreatePost(spaceListData).map(item => (
                            <div
                              key={item.id}
                              className={css.itemName}
                              value={item.id}
                              onClick={() => handleSpaceSelection(item.id)}
                            >
                              <span
                                className={
                                  item.policies.can_manage_space && item.policies.can_invite_members
                                    ? css.myGroup
                                    : css.joinedGroup
                                }
                              />
                              <span className={css.name}>{item.name}</span>
                            </div>
                          ))}
                        </div>
                      </OutsideClickHandler>
                    )}
                  </div>
                );
              }}
            />
            {!isEditMode && ['admin', 'moderator'].includes(userRoleInGroup) ? (
              <Field
                name="notificationOption"
                render={({ input }) => {
                  const notificationOptions = [
                    { value: 'none', label: 'No notification' },
                    { value: 'notification', label: 'Notify all group members' },
                    { value: 'notification_sms', label: 'Notify all group members + SMS' },
                  ];

                  const selectedNotification =
                    notificationOptions.find(opt => opt.value === input.value) ||
                    notificationOptions[0];

                  const handleNotificationSelection = optionValue => {
                    // Update the field value
                    input.onChange(optionValue);

                    // Also persist in formOldValues so topic/tag selection doesn't reset it
                    setFormOldValues(prevState => ({
                      ...prevState,
                      notificationOption: optionValue,
                      allmembersNotification:
                        optionValue === 'notification' || optionValue === 'notification_sms'
                          ? ['allmembersNotification']
                          : [],
                      allmembersNotificationbySms:
                        optionValue === 'notification_sms' ? ['allmembersNotification bySms'] : [],
                    }));

                    // Update hidden fields based on selection
                    if (optionValue === 'none') {
                      form.change('allmembersNotification', []);
                      form.change('allmembersNotificationbySms', []);
                    } else if (optionValue === 'notification') {
                      form.change('allmembersNotification', ['allmembersNotification']);
                      form.change('allmembersNotificationbySms', []);
                    } else if (optionValue === 'notification_sms') {
                      form.change('allmembersNotification', ['allmembersNotification']);
                      form.change('allmembersNotificationbySms', ['allmembersNotification bySms']);
                    }

                    setIsNotificationDropdownVisible(false);
                  };

                  return (
                    <div className={css.spaceIdBox}>
                      <div className={css.selectInput} onClick={toggleNotificationDropdown}>
                        <span>{selectedNotification.label}</span>
                      </div>

                      {isNotificationDropdownVisible && (
                        <OutsideClickHandler
                          onOutsideClick={() => setIsNotificationDropdownVisible(false)}
                        >
                          <div className={css.searchSpaceBox}>
                            {notificationOptions.map(option => (
                              <div
                                key={option.value}
                                className={css.itemName}
                                onClick={() => handleNotificationSelection(option.value)}
                              >
                                <span className={css.name}>{option.label}</span>
                              </div>
                            ))}
                          </div>
                        </OutsideClickHandler>
                      )}
                    </div>
                  );
                }}
              />
            ) : null}
            {/* Hidden fields to store notification values */}
            <Field name="allmembersNotification" component="input" type="hidden" />
            <Field name="allmembersNotificationbySms" component="input" type="hidden" />
            <div className={css.publishwrapper}>
              <Button
                type="submit"
                className={css.publishButton}
                disabled={
                  !isValid || limitExceed || requestCreatePost || !isOnline || isDropUploading
                }
                inProgress={requestCreatePost || isDropUploading}
              >
                {isEditMode ? 'Save changes' : <FormattedMessage id="CreatePost.publish" />}
              </Button>
            </div>
          </div>
        </div>
        {!isOnline && (
          <div className={css.errorMessage}>
            <FormattedMessage
              id="CreatePost.noInternetConnection"
              defaultMessage="No internet connection. Please check your network."
            />
          </div>
        )}
        <CoverImageUploadModal
          isImageModal={isCoverImageModal}
          onImageClick={onCoverImageClick}
          form={form}
          values={values}
          requestDirectUpload={requestDirectUpload}
          formOldValues={formOldValues}
          setFormOldValues={setFormOldValues}
        />
      </div>
    </Form>
  );
};

const CreatePostForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => <CreatePostFormFields {...fieldRenderProps} />}
  />
);

export default CreatePostForm;
